import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlayCircle, Users, Ticket, Lock, Unlock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, V1 } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { pickModuleAccess } from "@/lib/moduleAccess";

export default function MockExams() {
  const [exams, setExams] = useState([]);
  const [credits, setCredits] = useState(null);
  const [selected, setSelected] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  // Abonelik (unlimited/aylık kota) varsa premium denemeler kredi istemeden başlar —
  // kredi sadece abonelik yoksa/kota tükendiğinde fallback olarak devreye girer.
  const [premiumAccess, setPremiumAccess] = useState({ free: false, cost: 0 });
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`${V1}/mock-exams?upcoming=true`).then((r) => setExams(r.data || [])).catch(() => setExams([]));
    if (user) {
      api.get(`${V1}/users/me/credits`).then((r) => setCredits(r.data?.exam_credits ?? 0)).catch(() => {});
      api.get(`${V1}/users/me/usage`).then((r) => setPremiumAccess(pickModuleAccess(r.data, "premium_denemeler"))).catch(() => {});
    }
  }, [user]);

  const handleClick = (exam) => {
    if (!user) { navigate("/giris"); return; }
    if ((exam.credit_cost || 0) > 0 && !premiumAccess.free) {
      setSelected(exam);
      return;
    }
    startExam(exam);
  };

  const startExam = async (exam) => {
    try {
      const { data } = await api.post(`${V1}/exam-sessions/start`, { mock_exam_id: exam.id });
      sessionStorage.setItem(`sano_session_${data.session_id}`, JSON.stringify(data));
      if ((exam.credit_cost || 0) > 0) setCredits((c) => Math.max(0, (c ?? 0) - exam.credit_cost));
      navigate(`/sinav/${data.session_id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sınav başlatılamadı";
      toast.error(msg);
      if (e?.response?.status === 402) navigate("/panel/referans");
    }
  };

  const confirmPremium = async () => {
    if (!selected) return;
    setUnlocking(true);
    // brief unlock animation before navigation
    setTimeout(() => {
      startExam(selected);
    }, 900);
  };

  const insufficient = selected && credits != null && credits < (selected.credit_cost || 0);

  return (
    <div className="container mx-auto py-16 px-4 max-w-6xl">
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-accent mb-2">Online Deneme</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Deneme Sınavları</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Sunucu saatiyle senkronize güvenli sınav ortamı. LGS, TYT ve AYT formatına uygun haftalık ve aylık denemeler.
          </p>
        </div>
        {user && credits != null && (
          <Link to="/panel/referans" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-accent/40 bg-accent/5 hover:bg-accent/10 transition-colors" data-testid="mock-credits-chip">
            <Ticket className="h-4 w-4 text-accent" />
            <span className="text-sm">Kredin: <b>{credits}</b></span>
          </Link>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="mock-exams-page">
        {exams.length === 0 && <div className="text-muted-foreground p-16 col-span-3 text-center">Aktif deneme sınavı yok.</div>}
        {exams.map((e) => {
          const isPremium = (e.credit_cost || 0) > 0 && !premiumAccess.free;
          const isIncludedInPlan = (e.credit_cost || 0) > 0 && premiumAccess.free;
          return (
            <div
              key={e.id}
              className={`relative rounded-xl border p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
                isPremium
                  ? "border-accent/40 bg-gradient-to-br from-card to-accent/5 hover:border-accent"
                  : "border-border bg-card hover:border-primary/40"
              }`}
              data-testid={`mock-card-${e.id}`}
            >
              {isPremium && (
                <div
                  className="absolute -top-2 -right-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-bold shadow-md animate-pulse-slow"
                  data-testid={`mock-premium-badge-${e.id}`}
                >
                  <Ticket className="h-3 w-3" /> {e.credit_cost} Kredi
                </div>
              )}
              {isIncludedInPlan && (
                <div
                  className="absolute -top-2 -right-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-success text-white text-xs font-bold shadow-md"
                  data-testid={`mock-included-badge-${e.id}`}
                >
                  <Unlock className="h-3 w-3" /> Aboneliğine Dahil
                </div>
              )}
              <div className="overline text-accent mb-2">
                {e.exam_type}
                {(isPremium || isIncludedInPlan) && " · Premium"}
              </div>
              <div className="font-display font-bold text-xl mb-1">{e.title}</div>
              <div className="text-sm text-muted-foreground mb-4">{e.description}</div>
              <div className="grid grid-cols-3 gap-2 mb-6 mt-auto">
                <div className="p-3 rounded bg-muted text-center">
                  <div className="text-lg font-display font-bold">{e.duration_minutes}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dakika</div>
                </div>
                <div className="p-3 rounded bg-muted text-center">
                  <div className="text-lg font-display font-bold">{e.question_count}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Soru</div>
                </div>
                <div className="p-3 rounded bg-muted text-center">
                  <div className="text-lg font-display font-bold">−{e.negative_marking}</div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Negatif</div>
                </div>
              </div>
              <Button onClick={() => handleClick(e)} className="w-full group" data-testid={`start-mock-${e.id}`}>
                {isPremium ? (
                  <>
                    <Lock className="h-4 w-4 group-hover:hidden" />
                    <Unlock className="h-4 w-4 hidden group-hover:inline" />
                    {e.credit_cost} Kredi ile Katıl
                  </>
                ) : isIncludedInPlan ? (
                  <>
                    <Unlock className="h-4 w-4" /> Sınava Katıl (Ücretsiz)
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-4 w-4" /> Sınava Katıl
                  </>
                )}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Premium confirmation dialog with unlock animation */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && !unlocking && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="premium-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5 text-accent" /> Premium Deneme
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex justify-center py-6">
                <div className={`relative w-24 h-24 rounded-full flex items-center justify-center transition-all duration-700 ${
                  unlocking ? "bg-accent/20 scale-110" : "bg-muted"
                }`} data-testid="premium-lock-visual">
                  <Lock className={`h-10 w-10 text-accent absolute transition-all duration-500 ${
                    unlocking ? "opacity-0 -rotate-45 scale-50" : "opacity-100"
                  }`} />
                  <Unlock className={`h-10 w-10 text-success absolute transition-all duration-700 ${
                    unlocking ? "opacity-100 rotate-0 scale-110" : "opacity-0 rotate-45 scale-50"
                  }`} />
                  {unlocking && (
                    <Sparkles className="h-24 w-24 text-accent absolute animate-ping opacity-40" />
                  )}
                </div>
              </div>

              <div className="text-center">
                <div className="font-display font-bold text-lg">{selected.title}</div>
                <div className="text-sm text-muted-foreground">{selected.description}</div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg border border-border bg-muted/40 text-center">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Bakiyen</div>
                  <div className="text-2xl font-display font-bold" data-testid="premium-current-balance">{credits ?? 0}</div>
                </div>
                <div className="p-4 rounded-lg border border-accent/30 bg-accent/10 text-center">
                  <div className="text-xs uppercase tracking-widest text-accent mb-1">Maliyet</div>
                  <div className="text-2xl font-display font-bold text-accent" data-testid="premium-cost">
                    −{selected.credit_cost}
                  </div>
                </div>
              </div>

              {insufficient && (
                <div className="p-3 rounded-lg border border-destructive/40 bg-destructive/10 text-sm text-destructive text-center">
                  Yetersiz bakiye. Referans davetiyle kredi kazanabilirsin.
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {insufficient ? (
              <>
                <Button variant="outline" onClick={() => setSelected(null)}>Vazgeç</Button>
                <Button asChild data-testid="premium-earn-credits-btn">
                  <Link to="/panel/referans"><Ticket className="h-4 w-4" /> Kredi Kazan</Link>
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setSelected(null)} disabled={unlocking}>Vazgeç</Button>
                <Button onClick={confirmPremium} disabled={unlocking} data-testid="premium-confirm-btn">
                  {unlocking ? (
                    <><Unlock className="h-4 w-4 animate-bounce" /> Kilit açılıyor…</>
                  ) : (
                    <><Lock className="h-4 w-4" /> Onayla ve Başla</>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
